import { Injectable } from "@angular/core";
import { AdobeAnalyticsEvent } from "./adobe-analytics.events";
import { CtaType, DataLayerEvent } from "./adobe-analytics.models";

declare global {
  interface Window {
    dataLayer: DataLayerEvent[];
  }
}

@Injectable({ providedIn: "root" })
export class AdobeAnalyticsService {
  private _pendingBeacon = false;

  consumePendingBeacon(): boolean {
    const pending = this._pendingBeacon;
    this._pendingBeacon = false;
    return pending;
  }

  private get dataLayer(): DataLayerEvent[] | null {
    if (typeof window !== "undefined" && window.dataLayer) {
      return window.dataLayer;
    }
    return null;
  }

  private pushToDataLayer(event: DataLayerEvent, triggersBeacon = false): void {
    const dl = this.dataLayer;
    if (!dl) {
      if (typeof console !== "undefined") {
        console.warn("[AdobeAnalytics] dataLayer unavailable — event not pushed:", event.event);
      }
      return;
    }
    dl.push(event);
    if (triggersBeacon) {
      this._pendingBeacon = true;
    }
  }

  resetDataLayer(): void {
    const dl = this.dataLayer;
    if (dl) {
      dl.length = 0;
    }
  }

  processStart(processType: string, accountType?: string): void {
    this.pushToDataLayer(
      {
        event: AdobeAnalyticsEvent.PROCESS_START,
        process: {
          processType,
          ...(accountType && { accountType }),
        },
      },
      true,
    );
  }

  processStepSuccess(processStep: string, processType: string, accountType?: string): void {
    this.pushToDataLayer(
      {
        event: AdobeAnalyticsEvent.PROCESS_STEP_SUCCESS,
        process: {
          processStep,
          processType,
          ...(accountType && { accountType }),
        },
      },
      true,
    );
  }

  processComplete(processType: string, accountType?: string): void {
    this.pushToDataLayer(
      {
        event: AdobeAnalyticsEvent.PROCESS_COMPLETE,
        process: {
          processType,
          ...(accountType && { accountType }),
        },
      },
      true,
    );
  }

  processAbandoned(processType: string, processStep: string, abandonmentType: string): void {
    this.pushToDataLayer(
      {
        event: AdobeAnalyticsEvent.PROCESS_ABANDONED,
        process: {
          processType,
          processStep,
          processAbandonmentType: abandonmentType,
        },
      },
      true,
    );
  }

  callToAction(ctaName: string, location: string, ctaType: CtaType): void {
    this.pushToDataLayer(
      {
        event: AdobeAnalyticsEvent.CALL_TO_ACTION,
        callToAction: {
          ctaName,
          location,
          ctaType,
        },
        fireTrackLink: { fireTrackLink: "true" },
      },
      true,
    );
  }

  pushPageLoaded(
    pageName: string,
    pageTitle: string,
    siteSection1: string,
    siteSection2: string,
  ): void {
    this.pushToDataLayer({
      event: AdobeAnalyticsEvent.PAGE_LOADED,
      page: {
        pageName,
        pageTitle,
        siteSection1,
        siteSection2,
      },
    });
  }

  pushPageLoadCompleted(): void {
    this.pushToDataLayer({
      event: AdobeAnalyticsEvent.PAGE_LOAD_COMPLETED,
    });
  }
}
